import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
// Use pdfjs to render first page of a PDF (QSREPORT/letterhead) into an image when available
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';

export const QSReportScreen: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [exporting, setExporting] = useState(false);

  const handlePrint = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const win = iframe.contentWindow;
    if (win) {
      try {
        win.focus();
        win.print();
      } catch (e) {
        console.error('Print failed', e);
        alert('تعذر تنفيذ الطباعة من القالب مباشرة. جرب فتح القالب ثم الطباعة يدويًا.');
      }
    }
  }, []);

  const handleExportPdf = useCallback(async () => {
    const iframe = iframeRef.current;
    setExporting(true);
    try {
      // Prefer a ready background from public (robust & high fidelity)
      const tryLoadBackground = async (): Promise<{ dataUrl: string; fmt: 'PNG' | 'JPEG' } | null> => {
        // Helper: render first page of a PDF using pdfjs
        const tryPdf = async (path: string) => {
          try {
            GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            const loadingTask = getDocument(path);
            const pdf = await loadingTask.promise;
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 2 });
            const canvas = document.createElement('canvas');
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            const ctx = canvas.getContext('2d');
            if (!ctx) return null;
            await page.render({ canvasContext: ctx, viewport }).promise;
            const dataUrl = canvas.toDataURL('image/png');
            return { dataUrl, fmt: 'PNG' as const };
          } catch {
            return null;
          }
        };
        // Helper: load PNG/JPG from public
        const tryImage = async (path: string) => {
          try {
            const resp = await fetch(path);
            if (!resp.ok) return null;
            const blob = await resp.blob();
            const fr = new FileReader();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              fr.onload = () => resolve(String(fr.result));
              fr.onerror = reject;
              fr.readAsDataURL(blob);
            });
            const fmt = blob.type.includes('png') ? 'PNG' : 'JPEG';
            return { dataUrl, fmt } as const;
          } catch {
            return null;
          }
        };

        // Priority: QSREPORT first, then letterhead
        return (await tryPdf('/QSREPORT.pdf'))
          || (await tryPdf('/QSREPORT.pdf.pdf'))
          || (await tryImage('/QSREPORT.png'))
          || (await tryImage('/QSREPORT.png.png'))
          || (await tryPdf('/letterhead.pdf'))
          || (await tryImage('/letterhead.png'))
          || (await tryImage('/letterhead.jpg'))
          || null;
      };

      const bg = await tryLoadBackground();
      const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      if (bg) {
        // Render background full-page
        pdf.addImage(bg.dataUrl, bg.fmt, 0, 0, pageWidth, pageHeight);
        pdf.save('QSREPORT.pdf');
        return;
      }

      // Fallback: capture iframe contents if background files absent
      const doc = iframe?.contentDocument;
      const body = doc?.body;
      if (!body) {
        alert('لم يتم تحميل القالب بعد. يرجى الانتظار لحظة ثم المحاولة مرة أخرى.');
        return;
      }

      // Wait a tick to ensure layout/styles are settled
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(body, { useCORS: true, scale: 2, logging: false });
      const imgData = canvas.toDataURL('image/png');

      // Fit image while preserving aspect ratio
      const imgWidthPx = canvas.width;
      const imgHeightPx = canvas.height;
      const imgAspect = imgWidthPx / imgHeightPx;
      const pageAspect = pageWidth / pageHeight;

      let renderWidth = pageWidth;
      let renderHeight = pageHeight;
      if (imgAspect > pageAspect) {
        renderWidth = pageWidth;
        renderHeight = renderWidth / imgAspect;
      } else {
        renderHeight = pageHeight;
        renderWidth = renderHeight * imgAspect;
      }
      const offsetX = (pageWidth - renderWidth) / 2;
      const offsetY = (pageHeight - renderHeight) / 2;
      pdf.addImage(imgData, 'PNG', offsetX, offsetY, renderWidth, renderHeight);
      pdf.save('QSREPORT.pdf');
    } catch (error) {
      console.error('Export PDF error:', error);
      alert('حدث خطأ أثناء التصدير إلى PDF.');
    } finally {
      setExporting(false);
    }
  }, []);

  return (
    <div className="p-4 w-full h-full">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={handlePrint} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          طباعة
        </button>
        <button onClick={handleExportPdf} disabled={exporting} className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
          {exporting ? 'جاري التصدير...' : 'تصدير PDF'}
        </button>
        <a href="/QSREPORT.html" target="_blank" rel="noreferrer" className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
          فتح القالب مباشرة
        </a>
      </div>
      <div className="border rounded shadow overflow-hidden">
        <iframe
          ref={iframeRef}
          src="/QSREPORT.html"
          title="QSREPORT Template"
          className="w-full"
          style={{ height: '1200px' }}
        />
      </div>
    </div>
  );
};

export default QSReportScreen;